import type {
  AddDelayType,
  AddPlayers,
  BoardBuilderDurationStatProjections,
  BoardBuilderProjection,
  BoardBuilderProjections,
  BoardBuilderUpdateProjection,
  RankInfo
} from '@library/types/boardBuilder';
import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import { flatten, pluck, values } from 'ramda';
import {
  getDurationsWithRelatedIds,
  getLeagueDurationAttributes
} from '@bo-utils/leagues';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { Status } from '@library/types/projection';
import { getCalculatedBoardStartTimes } from '@bo-utils/leagueTimes';
import { getFilteredGameModesByDuration } from '@bo-utils/gameModes';
import { isTimeBefore } from '@library/utils/dateTime';
import { populatePropjectionsRank } from '@bo-utils/boardBuilderRankProjections';
import { processDurationStatProjections } from '@bo-utils/boardBuilderCreateProjections';
import { updateProjection } from '@bo-utils/boardBuilderUpdateProjections';
import { useBoardBuilderPublish } from '@bo-hooks/boardBuilder/useBoardBuilderPublish';
import { useImmer } from 'use-immer';

export type BulkAutoTradeChange = {
  autoTrade: boolean;
  durationId: string;
  statTypeId: string;
};

export const useBoardBuilderProjections = ({
  confirmedGameModes,
  confirmedPlayersList,
  confirmedStatTypes,
  durations,
  gameStartTimeDelay,
  gameStartTimes,
  leagueInfo,
  mainDurationId,
  onAddDelay,
  onResetFilters,
  onResetRankInfo,
  onResetSearch,
  rankInfo,
  selectedDurationId,
  statTypes
}: {
  confirmedGameModes: Array<FormattedGameMode>;
  confirmedPlayersList: AddPlayers;
  confirmedStatTypes: Array<string>;
  durations: Array<LeagueProjectionAttributes>;
  gameStartTimeDelay: AddDelayType;
  gameStartTimes: Record<string, string>;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  onAddDelay: ({ gameId, delay }: AddDelayType) => void;
  onResetFilters: () => void;
  onResetRankInfo: () => void;
  onResetSearch: () => void;
  rankInfo: RankInfo;
  selectedDurationId: string;
  statTypes: Array<LeagueProjectionAttributes>;
}) => {
  const [durationStatProjections, setDurationStatProjections] =
    useImmer<BoardBuilderDurationStatProjections>({});

  const confirmedGameModesRef = useRef(confirmedGameModes);

  const {
    handlePublishProjections,
    isPublishProjectionsError,
    isPublishProjectionsSuccess,
    isPublishProjectionsUninitialized,
    publishProjectionsError,
    resetPublishProjectionsApi
  } = useBoardBuilderPublish();

  const handleProcessProjections = useCallback(() => {
    processDurationStatProjections({
      confirmedGameModes,
      confirmedPlayersList,
      confirmedStatTypesList: confirmedStatTypes,
      durations,
      leagueInfo,
      mainDurationId: mainDurationId ?? '',
      setDurationStatProjections,
      statTypes
    });
  }, [
    confirmedGameModes,
    confirmedPlayersList,
    confirmedStatTypes,
    durations,
    leagueInfo,
    mainDurationId,
    setDurationStatProjections,
    statTypes
  ]);

  const handleRankProjections = useCallback(
    (startRank: number) => {
      populatePropjectionsRank({
        confirmedStatTypes,
        durations,
        startRank,
        setDurationStatProjections
      });
    },
    [confirmedStatTypes, durations, setDurationStatProjections]
  );

  const handleProjectionsChange = useCallback(
    ({
      durations: durationsList,
      initialProjection,
      key,
      leagueInfo: leagueData,
      mainDurationId: firstDurationId,
      projectionId,
      value
    }: BoardBuilderUpdateProjection) => {
      updateProjection({
        durations: durationsList,
        initialProjection,
        key,
        leagueInfo: leagueData,
        mainDurationId: firstDurationId,
        projectionId,
        setDurationStatProjections,
        value
      });
    },
    [setDurationStatProjections]
  );

  const handleToggleDurationStatTypeAllProjectionsStatus = useCallback(
    ({
      durationId,
      statTypeId,
      status
    }: {
      durationId: string;
      statTypeId: string;
      status: Status;
    }) => {
      setDurationStatProjections((draft) => {
        const projections =
          draft[durationId].statTypes[statTypeId]?.projections;

        projections.forEach((_projection, index) => {
          projections[index].status = status;
        });
      });
    },
    [setDurationStatProjections]
  );

  const handleResetProjections = useCallback(() => {
    setDurationStatProjections({});
  }, [setDurationStatProjections]);

  const handleResetAll = useCallback(() => {
    handleResetProjections();
    onResetFilters();
    onResetSearch();
  }, [handleResetProjections, onResetFilters, onResetSearch]);

  const isPublishSuccess =
    !isPublishProjectionsUninitialized && isPublishProjectionsSuccess;

  // Reset Publish Projections API and durationStatProjections once the league has changed
  useEffect(() => {
    if (isPublishSuccess) {
      resetPublishProjectionsApi();
      handleResetProjections();
    }
  }, [handleResetProjections, resetPublishProjectionsApi, isPublishSuccess]);

  // Reset Search Input, Player Selection and Stat Type Selection once Projections are saved successfully
  useEffect(() => {
    if (isPublishSuccess) {
      handleResetAll();
    }
  }, [handleResetAll, isPublishSuccess]);

  // Rank Projections and reset Rank info once the rank info is received
  useEffect(() => {
    if (rankInfo.populateRank && !!rankInfo.rank) {
      handleRankProjections(rankInfo.rank);
      onResetRankInfo();
    }
  }, [handleRankProjections, onResetRankInfo, rankInfo]);

  // Create/Update/Remove projections once the confirmed Players/Stat Types are changed
  useEffect(() => {
    handleProcessProjections();
  }, [
    confirmedGameModes,
    confirmedPlayersList,
    confirmedStatTypes,
    handleProcessProjections
  ]);

  const selectedDurationStatData = useMemo(
    () =>
      durationStatProjections
        ? durationStatProjections[selectedDurationId]
        : undefined,
    [durationStatProjections, selectedDurationId]
  );

  const selectedDurationStatProjections = useMemo(
    () =>
      selectedDurationStatData ? selectedDurationStatData.statTypes : undefined,
    [selectedDurationStatData]
  );

  const allProjections: Array<BoardBuilderProjection> = useMemo(() => {
    if (!durationStatProjections) {
      return [];
    }

    const allDurationStatProjections = values(durationStatProjections);
    const allStatValues = pluck('statTypes', allDurationStatProjections);
    const allStatProjectionsList = allStatValues.map((statProjectionList) =>
      values(statProjectionList)
    );

    const allProjectionsList = flatten(allStatProjectionsList);
    const projectionsList = pluck('projections', allProjectionsList);
    return flatten(projectionsList);
  }, [durationStatProjections]);

  const leaguesDurationsAttributes = useMemo(
    () =>
      durations.map((duration) => {
        return getLeagueDurationAttributes({
          durationId: duration.leagueProjectionFilterId,
          leagueInfo
        });
      }),
    [durations, leagueInfo]
  );

  const handleBulkAutoTradeChange = useCallback(
    ({ autoTrade, durationId, statTypeId }: BulkAutoTradeChange) => {
      allProjections
        .filter((projection) => {
          if (
            projection.statTypeId !== statTypeId ||
            projection.durationId !== durationId ||
            projection.autoTrade === autoTrade
          ) {
            return false;
          }

          const startDateTime = DateTime.fromISO(projection.startTime);
          const isPastStartTime = isTimeBefore({ dateValue: startDateTime });
          return !(isPastStartTime || projection.isPromo);
        })
        .forEach((projection) => {
          handleProjectionsChange({
            durations,
            initialProjection: projection,
            key: 'autoTrade',
            leagueInfo,
            mainDurationId,
            projectionId: projection.projectionId,
            value: autoTrade
          });
        });
    },
    [
      allProjections,
      durations,
      handleProjectionsChange,
      leagueInfo,
      mainDurationId
    ]
  );

  const handleBulkChangeTime = useCallback(
    ({
      durationId,
      statTypeId,
      boardTime,
      startTime,
      displayTime,
      isTopLevelChange
    }: {
      durationId?: string;
      statTypeId?: string;
      boardTime?: DateTime;
      startTime?: DateTime;
      displayTime?: DateTime;
      isTopLevelChange?: boolean;
    }) => {
      const boardTimeString = boardTime?.toISO();
      const startTimeString = startTime?.toISO();
      const displayTimeString = displayTime?.toISO();

      if (!isTopLevelChange && durationId && statTypeId) {
        const affectedProjections: BoardBuilderProjections = [];

        const addPreGameProjections = (id: string) => {
          const statTypeProjections =
            durationStatProjections[id]?.statTypes[statTypeId];
          if (statTypeProjections?.projections) {
            statTypeProjections.projections.forEach((projection) => {
              if (projection.status === 'pre_game') {
                affectedProjections.push(projection);
              }
            });
          }
        };

        if (startTime || displayTime) {
          const allLeagueDurationRelationships = getDurationsWithRelatedIds({
            durations,
            isMoreThanParentRelation: true
          });

          const relatedDurationIds = allLeagueDurationRelationships.find(
            (duration) => duration.leagueProjectionFilterId === durationId
          )?.leagueProjectionFilterRelatedIds;

          relatedDurationIds?.forEach((id) => {
            if (id) {
              addPreGameProjections(id);
            }
          });
        } else {
          addPreGameProjections(durationId);
        }

        affectedProjections.forEach((projection) => {
          if (boardTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'boardTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: boardTimeString
            });
          }

          if (startTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'startTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: startTimeString
            });
          }

          if (displayTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'displayTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: displayTimeString
            });
          }
        });
      } else {
        allProjections.forEach((projection) => {
          if (projection.status !== 'pre_game') {
            return;
          }

          if (boardTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'boardTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: boardTimeString || ''
            });
          }

          if (startTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'startTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: startTimeString || ''
            });
          }

          if (displayTimeString) {
            handleProjectionsChange({
              durations,
              initialProjection: projection,
              key: 'displayTime',
              leagueInfo,
              mainDurationId,
              projectionId: projection.projectionId,
              value: displayTimeString || ''
            });
          }
        });
      }
    },
    [
      allProjections,
      durationStatProjections,
      durations,
      handleProjectionsChange,
      leagueInfo,
      mainDurationId
    ]
  );

  const handleAddDelay = useCallback(
    ({
      gameId,
      delay,
      isTimePickerChange
    }: {
      gameId: string;
      delay: number;
      isTimePickerChange: boolean;
    }) => {
      allProjections.forEach((projection) => {
        const startDateTime = DateTime.fromISO(projection.startTime);
        const isPastStartTime = isTimeBefore({ dateValue: startDateTime });
        const isProjectionValueLocked = isPastStartTime || projection.isPromo;

        if (
          projection.gameId === gameId &&
          projection.status !== 'removed' &&
          !isProjectionValueLocked
        ) {
          const leagueName =
            leaguesDurationsAttributes.find(
              (league) => league.leagueId === projection.leagueId
            )?.name || '';

          const startTimeWithDelay = isTimePickerChange
            ? (DateTime.fromISO(gameStartTimes[projection.gameId])
                .plus({ minutes: delay })
                .toISO() ?? '')
            : (DateTime.fromISO(
                startDateTime.toISO() || gameStartTimes[projection.gameId]
              )
                .plus({ minutes: delay })
                .toISO() ?? '');

          const { startTime: calculatedStartTime } =
            getCalculatedBoardStartTimes({
              durationId: projection.durationId || '',
              durations,
              gameStartTime: startTimeWithDelay,
              leagueName
            });

          handleProjectionsChange({
            durations,
            initialProjection: projection,
            key: 'startTime',
            leagueInfo,
            mainDurationId,
            projectionId: projection.projectionId,
            value: calculatedStartTime
          });
        }
      });
    },
    [
      allProjections,
      durations,
      gameStartTimes,
      handleProjectionsChange,
      leagueInfo,
      leaguesDurationsAttributes,
      mainDurationId
    ]
  );

  /* Update the projections on Add Game Start Time Delay */
  useEffect(() => {
    const { gameId, delay, isTimePickerChange } = gameStartTimeDelay;

    if (gameId && delay > 0) {
      handleAddDelay({ gameId, delay, isTimePickerChange });
      onAddDelay({ gameId: '', delay: 0, isTimePickerChange: false });
    }
  }, [gameStartTimeDelay, handleAddDelay, onAddDelay]);

  const handleTopLevelGameModesChange = useCallback(() => {
    const confirmedGameModeIds = confirmedGameModes.map(({ id }) => id);

    allProjections.forEach((projection) => {
      if (projection.status !== 'pre_game') {
        return;
      }

      const isInvalidGameModeChecked = projection.gameModeIds?.some(
        (id) => !confirmedGameModeIds.includes(id)
      );

      if (isInvalidGameModeChecked) {
        handleProjectionsChange({
          durations,
          initialProjection: projection,
          key: 'gameModeIds',
          leagueInfo,
          mainDurationId,
          projectionId: projection.projectionId,
          value: confirmedGameModeIds
        });
      }
    });
  }, [
    allProjections,
    confirmedGameModes,
    durations,
    handleProjectionsChange,
    leagueInfo,
    mainDurationId
  ]);

  /* Update projections gameModeIds on top level Game Mode change */
  useEffect(() => {
    if (confirmedGameModesRef.current !== confirmedGameModes) {
      confirmedGameModesRef.current = confirmedGameModes;
      handleTopLevelGameModesChange();
    }
  }, [confirmedGameModes, handleTopLevelGameModesChange]);

  const filteredConfirmedGameModes = useMemo(
    () =>
      getFilteredGameModesByDuration(
        confirmedGameModes,
        durations,
        selectedDurationId
      ),
    [confirmedGameModes, durations, selectedDurationId]
  );

  return {
    allProjections,
    filteredConfirmedGameModes,
    handleBulkChangeTime,
    handleBulkAutoTradeChange,
    handleProjectionsChange,
    handlePublishProjections,
    handleRankProjections,
    handleResetAll,
    handleResetProjections,
    handleToggleDurationStatTypeAllProjectionsStatus,
    isPublishProjectionsError,
    isPublishProjectionsSuccess,
    isPublishProjectionsUninitialized,
    isPublishSuccess,
    projectionRows: selectedDurationStatProjections,
    publishProjectionsError,
    resetPublishProjectionsApi,
    setShowSuccessBanner: () => {},
    showSuccessBanner: false
  };
};
